import { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import { api } from '../../frontend/components/api';

/**
 * Custom hook to initialize Twilio.Device and expose connect/disconnect controls.
 *
 * @param {object}   voiceConfiguration   – configuration object (must include bot_number and optionally client_token).
 * @param {string}   selectedInputDeviceId        – The deviceId of the chosen audio input (microphone).
 * @param {string}   selectedOutputDeviceId       – The deviceId of the chosen audio output (speaker/headphones).
 *
 * @returns {{
 *   status: string,
 *   error: string | null,
 *   device: Device | null,
 *   connection: any,
 *   isMuted: boolean,
 *   audioStream: MediaStream | null,
 *   conversation: any,
 *   canConnect: boolean,
 *   canDisconnect: boolean,
 *   canToggleMute: boolean,
 *   canRestart: boolean,
 *   handleConnect: () => Promise<void>,
 *   handleDisconnect: () => void,
 *   handleToggleMute: () => void,
 *   handleRestartCallSession: () => Promise<void>
 * }}
 */
export function useTwilioVoice(
  voiceConfiguration,
  selectedInputDeviceId,
  selectedOutputDeviceId
) {
  const [status, setStatus] = useState('Initializing...');
  const [error, setError] = useState(null);
  const [device, setDevice] = useState(null);
  const [connection, setConnection] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [conversation, setConversation] = useState(null);

  const deviceRef = useRef(null);
  const isMountedRef = useRef(true);
  const disconnectTimeoutRef = useRef(null);

  /**
   * initializeDevice()
   *
   * - Fetches a Twilio token from your API.
   * - Constructs a new Twilio.Device(token).
   * - Hooks up all event listeners (ready, connect, disconnect, error, offline).
   * - Registers the Device.
   */
  const initializeDevice = useCallback(async () => {
    setStatus('Fetching token...');
    setError(null);
    try {
      // 1) Fetch a Twilio Access Token from your backend
      const response =
        (await api.fetchTwilioAccessTokenPublic?.(
          voiceConfiguration?.client_token
        )) ?? (await api.fetchTwilioAccessToken?.());
      const { token, conversation } = response || {};
      if (!token) throw new Error('Invalid token received from server.');
      setConversation(conversation || null);

      // 2) Instantiate Twilio.Device
      setStatus('Initializing device...');
      const currentDevice = new Device(token, { logLevel: 'WARN' });
      deviceRef.current = currentDevice;

      // 3) When Device becomes “ready”, we can set its status to “Ready”,
      //    expose it to state, and apply any pre‐selected input/output devices.
      currentDevice.on('ready', () => {
        if (!isMountedRef.current) return;

        setStatus('Ready');
        setDevice(currentDevice);

        // If parent has already passed in selectedInput/selectedOutput,
        // configure them here as soon as the device is ready:
        if (selectedInputDeviceId) {
          try {
            currentDevice.audio.setInputDevice(selectedInputDeviceId);
          } catch (err) {
            console.warn('Failed to set input device on ready:', err);
          }
        }
        if (selectedOutputDeviceId) {
          try {
            currentDevice.audio.speakerDevices.set(selectedOutputDeviceId);
          } catch (err) {
            console.warn('Failed to set output device on ready:', err);
          }
        }
      });

      // 4) Handle a new “connect” (i.e. outbound call has connected).
      currentDevice.on('connect', (conn) => {
        if (!isMountedRef.current) return;
        if (!conn || typeof conn.disconnect !== 'function') {
          setError('Received invalid connection object from Twilio.');
          setStatus('Error');
          return;
        }

        setStatus('On Call');
        setConnection(conn);
        setIsMuted(conn.isMuted());

        // Poll until we can grab the remote audio stream, then store it in state
        const handle = setInterval(() => {
          if (isMountedRef.current && conn.getRemoteStream()) {
            setAudioStream(conn.getRemoteStream());
            clearInterval(handle);
          }
        }, 100);

        // Listen for in‐call events:
        conn.on('mute', (muted) => {
          if (isMountedRef.current) setIsMuted(muted);
        });
        conn.on('disconnect', () => {
          if (!isMountedRef.current) return;
          clearTimeout(disconnectTimeoutRef.current);
          setStatus('Ready');
          setConnection(null);
          setAudioStream(null);
        });
        conn.on('error', (err) => {
          if (!isMountedRef.current) return;
          setError(`Call Error: ${err.message} (Code: ${err.code})`);
          setStatus('Error');
          setConnection(null);
          setAudioStream(null);
        });
      });

      // 5) Handle a generic “disconnect” (could be triggered by Device.destroy(), network loss, etc.)
      currentDevice.on('disconnect', () => {
        if (!isMountedRef.current) return;
        clearTimeout(disconnectTimeoutRef.current);
        setStatus('Ready');
        setConnection(null);
        setAudioStream(null);
      });

      // 6) Handle a Device‐level error (registration/capability failure, etc.)
      currentDevice.on('error', (err) => {
        if (!isMountedRef.current) return;
        setError(`Device Error: ${err.message} (Code: ${err.code})`);
        setStatus('Error');
      });

      // 7) If Twilio.Device goes offline (e.g. network dropped), mark it:
      currentDevice.on('offline', () => {
        if (!isMountedRef.current) return;
        setStatus('Offline');
        setDevice(null);
      });

      // 8) Finally, register the device with Twilio’s servers
      await currentDevice.register();
      if (isMountedRef.current && currentDevice.state === 'registered') {
        setStatus('Ready');
        setDevice(currentDevice);
      }
    } catch (err) {
      console.error('Initialization failed:', err);
      if (isMountedRef.current) {
        setError(`Initialization failed: ${err?.message}`);
        setStatus('Error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceConfiguration]);

  /**
   * On mount: initializeDevice(), and tear everything down on unmount.
   */
  useEffect(() => {
    if (!voiceConfiguration) return;
    isMountedRef.current = true;
    initializeDevice();

    return () => {
      isMountedRef.current = false;
      clearTimeout(disconnectTimeoutRef.current);

      if (deviceRef.current) {
        deviceRef.current.removeAllListeners();
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      setDevice(null);
      setConnection(null);
      setStatus('Unmounted');
      setError(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeDevice]);

  /**
   * Whenever `selectedInput` changes AND the Device is ready,
   * call Device.audio.setInputDevice(...) to switch microphones.
   */
  useEffect(() => {
    if (!deviceRef.current) return;
    if (!selectedInputDeviceId) return;

    try {
      deviceRef.current.audio.setInputDevice(selectedInputDeviceId);
    } catch (err) {
      console.warn('Failed to set input device:', err);
    }
  }, [selectedInputDeviceId]);

  /**
   * Whenever `selectedOutput` changes AND the Device is ready,
   * call Device.audio.setOutputDevice(...) to switch speakers/headphones.
   */
  useEffect(() => {
    if (!deviceRef.current) return;
    if (!selectedOutputDeviceId) return;

    try {
      deviceRef.current.audio.speakerDevices.set(selectedOutputDeviceId);
    } catch (err) {
      console.warn('Failed to set output device:', err);
    }
  }, [selectedOutputDeviceId]);

  /**
   * handleConnect()
   *
   * - Initiates an outbound call to your bot_number.
   * - Updates status/connection/audioStream in state.
   */
  const handleConnect = useCallback(async () => {
    if (!device) {
      setError('Device not ready.');
      return;
    }

    setStatus('Connecting...');
    try {
      const call = await device.connect({
        params: { To: voiceConfiguration.bot_number },
      });

      if (!call || typeof call.disconnect !== 'function') {
        setError('Connection attempt returned an invalid object.');
        setStatus('Error');
        return;
      }

      setStatus('On Call');
      setConnection(call);
      setIsMuted(call.isMuted());

      // Poll until remote audio stream is available
      const handle = setInterval(() => {
        if (isMountedRef.current && call.getRemoteStream()) {
          setAudioStream(call.getRemoteStream());
          clearInterval(handle);
        }
      }, 100);

      // Bind in‐call event listeners:
      call.on('disconnect', () => {
        if (!isMountedRef.current) return;
        clearTimeout(disconnectTimeoutRef.current);
        setStatus('Ready');
        setConnection(null);
        setAudioStream(null);
      });
      call.on('error', (err) => {
        if (!isMountedRef.current) return;
        setError(`Call Error: ${err.message} (Code: ${err.code})`);
        setStatus('Error');
        setConnection(null);
        setAudioStream(null);
      });
      call.on('mute', (muted) => {
        if (isMountedRef.current) setIsMuted(muted);
      });
    } catch (err) {
      setError(`Connection Error: ${err.message}`);
      setStatus('Ready');
    }
  }, [device, voiceConfiguration]);

  /**
   * handleDisconnect():
   * - Disconnect the current call (or all calls) and update status.
   */
  const handleDisconnect = useCallback(() => {
    clearTimeout(disconnectTimeoutRef.current);
    if (connection) {
      connection.disconnect();
    } else if (device) {
      device.disconnectAll();
    }
    setStatus('Disconnecting...');
    disconnectTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      setStatus((prev) => {
        if (prev === 'Disconnecting...') {
          setConnection(null);
          return 'Ready';
        }
        return prev;
      });
    }, 5000);
  }, [connection, device]);

  /**
   * handleToggleMute():
   * - Mutes/unmutes the current connection.
   */
  const handleToggleMute = useCallback(() => {
    if (!connection) return;
    const shouldMute = !isMuted;
    connection.mute(shouldMute);
    setIsMuted(shouldMute);
  }, [connection, isMuted]);

  /**
   * handleRestartCallSession():
   * - Ends any active call/session and re-initializes Twilio.Device with a fresh token.
   * - This creates a new conversation context similar to web chat reset behavior.
   */
  const handleRestartCallSession = useCallback(async () => {
    if (!voiceConfiguration) return;

    clearTimeout(disconnectTimeoutRef.current);
    setError(null);
    setStatus('Restarting...');

    try {
      if (connection) {
        connection.disconnect();
      } else if (deviceRef.current) {
        deviceRef.current.disconnectAll();
      }

      if (deviceRef.current) {
        deviceRef.current.removeAllListeners();
        deviceRef.current.destroy();
        deviceRef.current = null;
      }

      setDevice(null);
      setConnection(null);
      setAudioStream(null);
      setIsMuted(false);
      setConversation(null);

      await initializeDevice();
    } catch (err) {
      setError(`Restart Error: ${err?.message || 'Unknown error'}`);
      setStatus('Error');
    }
  }, [connection, initializeDevice, voiceConfiguration]);

  const canRestart =
    Boolean(voiceConfiguration) &&
    !connection &&
    ![
      'Fetching token...',
      'Initializing device...',
      'Connecting...',
      'Restarting...',
      'Disconnecting...',
      'On Call',
    ].includes(status);

  return {
    status,
    error,
    device,
    connection,
    isMuted,
    audioStream,
    conversation,
    canConnect: Boolean(device && (status === 'Ready' || status === 'Offline')),
    canDisconnect: Boolean(connection),
    canToggleMute: Boolean(connection),
    canRestart,
    handleConnect,
    handleDisconnect,
    handleToggleMute,
    handleRestartCallSession,
  };
}
